import {
  Box,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogDescription,
  DialogHeader,
  ScrollArea,
  Text,
  Video,
} from "@prismicio/editor-ui"
import { type RefObject, useEffect, useState } from "react"

import type { OnboardingStep } from "../../service"

interface OnboardingStepDialogProps {
  isOpen: boolean
  step: OnboardingStep
  stepIndex: number
  isStepComplete: boolean
  onActionClick: (isUndo?: boolean) => Promise<void>
  onClose: () => void
  contentRef: RefObject<HTMLDivElement>
}

export function OnboardingStepDialog(props: OnboardingStepDialogProps) {
  const { step, stepIndex, isOpen, onActionClick, onClose, contentRef } = props

  const [isToggling, setToggling] = useState(false)
  const [isStepComplete, setStepComplete] = useState(props.isStepComplete)

  useEffect(() => {
    // avoid updating the button label when the dialog is closing
    isOpen && setStepComplete(props.isStepComplete)
  }, [props.isStepComplete, isOpen])

  async function handleAction() {
    if (!isOpen) return
    try {
      setToggling(true)
      await onActionClick(isStepComplete)
    } finally {
      setToggling(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose} size="small">
      <DialogHeader title="Onboarding" />
      <DialogContent>
        <ScrollArea>
          <Box
            ref={contentRef}
            as="article"
            flexDirection="column"
            padding={16}
            gap={16}
          >
            <DialogDescription asChild>
              <section>
                <Text sx={{ marginBottom: 4 }} color="purple9" variant="bold">
                  Step {stepIndex + 1}
                </Text>

                <Text variant="h3">{step.title}</Text>
                {step.content}
              </section>
            </DialogDescription>
            {step.videoUrl && (
              <Video
                src={step.videoUrl}
                sizing="contain"
                borderRadius={4}
                autoPlay
                loop
              />
            )}
          </Box>
        </ScrollArea>
        <DialogActions>
          <DialogCancelButton size="medium">Close</DialogCancelButton>
          {!step.defaultCompleted && (
            <DialogActionButton
              size="medium"
              onClick={handleAction}
              loading={isToggling}
              color={isStepComplete ? "darkGrey" : "purple"}
            >
              {isStepComplete ? "Undo step" : "Complete step"}
            </DialogActionButton>
          )}
        </DialogActions>
      </DialogContent>
    </Dialog>
  )
}
